import React, { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Navigate, useOutletContext } from 'react-router-dom';
import { LINKS, PAYMENT_QR_SRC } from '../constants/links';
import { openExternalUrl } from '../utils/openExternalUrl';
import { isNativeApp } from '../utils/platform';
import { useMountTransition } from '../hooks/useMountTransition';
import { activateClose, dismissFocus } from '../utils/dismissFocus';
import isoSecure from '../assets/bank-secure-192.webp';
import './styles/Payment.css';

const PaymentQrDialog = ({ open, onClose }) => {
  const { mounted, active } = useMountTransition(open, 320);

  const handleClose = useCallback(
    (event) => {
      dismissFocus(event);
      onClose?.();
    },
    [onClose]
  );

  useEffect(() => {
    if (!mounted) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') handleClose();
    };
    document.addEventListener('keydown', onKey);
    document.body.classList.add('kkt-dialog-open');
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.classList.remove('kkt-dialog-open');
      document.body.style.overflow = prev;
    };
  }, [mounted, handleClose]);

  if (!mounted || typeof document === 'undefined') return null;

  return createPortal(
    <>
      <div
        className={`kkt-blur-backdrop${active ? ' is-visible' : ''}`}
        onClick={handleClose}
        role="presentation"
      />
      <div
        className={`payment-qr-dialog${active ? ' is-visible' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="payment-qr-title"
      >
        <header className="payment-qr-dialog__head">
          <h2 id="payment-qr-title">
            <i className="bi bi-qr-code" aria-hidden="true"></i>
            QR-код для оплаты
          </h2>
          <button
            type="button"
            className="payment-qr-dialog__close"
            onPointerDown={(event) => activateClose(event, handleClose)}
            onClick={(event) => activateClose(event, handleClose)}
            aria-label="Закрыть"
          >
            <i className="bi bi-x-lg"></i>
          </button>
        </header>
        <p className="payment-qr-dialog__lead">
          Отсканируйте камерой телефона в приложении банка
        </p>
        <div className="payment-qr-dialog__frame">
          <img src={PAYMENT_QR_SRC} alt="QR-код для оплаты обучения ВТБ" />
        </div>
        <button type="button" className="payment-qr-dialog__ok" onClick={handleClose}>
          Закрыть
        </button>
      </div>
    </>,
    document.body
  );
};

const Payment = () => {
  const { user } = useOutletContext() || {};
  const [qrOpen, setQrOpen] = useState(false);
  const nativeApp = isNativeApp();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  if (user && user.role && user.role !== 'student') {
    return <Navigate to="/curatorship" replace />;
  }

  return (
    <div className="payment-page">
      <section className="payment-panel">
        <div className="payment-panel__top">
          <div className="payment-secure">
            <img
              className="payment-secure__icon"
              src={isoSecure}
              alt=""
              width={96}
              height={98}
              decoding="async"
            />
            <div className="payment-secure__body">
              <h1>Безопасная оплата обучения</h1>
              <p>
                {nativeApp
                  ? 'Оплата откроется в браузере или приложении ВТБ. Данные карты в расписании не вводятся и не сохраняются.'
                  : 'Оплата проходит на стороне ВТБ. Данные карты в приложении расписания не вводятся и не сохраняются.'}
              </p>
              <a
                href={LINKS.payment}
                className="payment-cta payment-cta--inline"
                target="_blank"
                rel="noopener noreferrer"
                onClick={(event) => {
                  event.preventDefault();
                  openExternalUrl(LINKS.payment);
                }}
              >
                <i className="bi bi-box-arrow-up-right"></i>
                Оплатить онлайн
              </a>
            </div>
          </div>

          <div className="payment-panel__qr" aria-label="QR для оплаты">
            <img src={PAYMENT_QR_SRC} alt="QR-код для оплаты обучения ВТБ" />
            <p>Наведите камеру банка на QR</p>
          </div>
        </div>

        <div className="payment-panel__howto">
          <h2>Как оплатить</h2>
          <ol className="payment-steps">
            <li>
              {nativeApp
                ? 'Нажмите «Оплатить онлайн» — платёж откроется вне приложения'
                : 'Нажмите «Оплатить онлайн» или отсканируйте QR-код'}
            </li>
            <li>Откройте платёж в приложении банка / на сайте ВТБ</li>
            <li>Проверьте сумму и подтвердите оплату</li>
          </ol>

          <div className="payment-actions">
            <a
              href={LINKS.payment}
              className="payment-cta payment-cta--mobile-only"
              target="_blank"
              rel="noopener noreferrer"
              onClick={(event) => {
                event.preventDefault();
                openExternalUrl(LINKS.payment);
              }}
            >
              <i className="bi bi-box-arrow-up-right"></i>
              Оплатить онлайн
            </a>
            <button
              type="button"
              className="payment-cta payment-cta--secondary payment-cta--mobile-only"
              onClick={() => setQrOpen(true)}
            >
              <i className="bi bi-qr-code"></i>
              Открыть QR
            </button>
          </div>
        </div>
      </section>

      <PaymentQrDialog open={qrOpen} onClose={() => setQrOpen(false)} />
    </div>
  );
};

export default Payment;
